/**
 * TokenManager - Centralized token management to prevent race conditions
 */
class TokenManager {
  constructor() {
    this.tokenPromise = null;
    this.cachedToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Get authentication token with caching and deduplication
   * @returns {Promise<string|null>} Authentication token or null if failed
   */
  async getToken() {
    // Return cached token if still valid WITH BUFFER TIME
    // Use 2-minute buffer to account for network delays and ensure token is fresh
    const bufferTime = 2 * 60 * 1000; // 2 minutes in milliseconds
    if (this.cachedToken && this.tokenExpiry && Date.now() < (this.tokenExpiry - bufferTime)) {
      console.log('🔐 TokenManager: Using cached token');
      return this.cachedToken;
    }

    // Clear expired or soon-to-expire token immediately
    if (this.cachedToken && this.tokenExpiry && Date.now() >= (this.tokenExpiry - bufferTime)) {
      console.log('🔐 TokenManager: Token expired or expiring soon, clearing cache');
      this.clearToken();
    }

    // If a token fetch is already in progress, wait for it
    if (this.tokenPromise) {
      console.log('🔐 TokenManager: Token fetch already in progress, waiting...');
      return await this.tokenPromise;
    }

    // Start new token fetch
    console.log('🔐 TokenManager: Starting new token fetch');
    this.tokenPromise = this._fetchNewToken();
    
    try {
      const token = await this.tokenPromise;
      return token;
    } finally {
      // Clear the promise so future calls can start a new fetch
      this.tokenPromise = null;
    }
  }

  /**
   * Internal method to fetch a new token
   * @private
   */
  async _fetchNewToken() {
    try {
      const { fetchAuthSession } = await import('@aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        this.cachedToken = token;
        // Cache for 45 minutes (tokens typically expire in 60 minutes)
        // This gives us 15 minutes buffer before actual expiration
        const cacheTime = 45 * 60 * 1000;
        this.tokenExpiry = Date.now() + cacheTime;
        
        const expiryDate = new Date(this.tokenExpiry);
        console.log('🔐 TokenManager: Token fetched and cached successfully');
        console.log(`🔐 TokenManager: Token will be refreshed at ${expiryDate.toLocaleTimeString()}`);
      } else {
        console.warn('🔐 TokenManager: No token received from fetchAuthSession');
      }
      
      return token;
    } catch (error) {
      console.warn('🔐 TokenManager: Failed to get auth token:', error);
      this.cachedToken = null;
      this.tokenExpiry = null;
      return null;
    }
  }

  /**
   * Clear cached token (e.g., on logout or 401 error)
   */
  clearToken() {
    console.log('🔐 TokenManager: Clearing cached token');
    this.cachedToken = null;
    this.tokenExpiry = null;
    this.tokenPromise = null;
  }

  /**
   * Get token status for debugging
   * @returns {Object} Token status information
   */
  getTokenStatus() {
    const now = Date.now();
    const bufferTime = 2 * 60 * 1000; // 2 minutes
    
    return {
      hasToken: !!this.cachedToken,
      hasExpiry: !!this.tokenExpiry,
      isValid: this.cachedToken && this.tokenExpiry && now < (this.tokenExpiry - bufferTime),
      timeUntilExpiry: this.tokenExpiry ? Math.max(0, this.tokenExpiry - now) : 0,
      timeUntilRefresh: this.tokenExpiry ? Math.max(0, (this.tokenExpiry - bufferTime) - now) : 0,
      expiryDate: this.tokenExpiry ? new Date(this.tokenExpiry).toLocaleString() : null,
      refreshDate: this.tokenExpiry ? new Date(this.tokenExpiry - bufferTime).toLocaleString() : null
    };
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
